import React, { useState, useEffect } from 'react';
import { X, Calendar, CheckCircle, Trash2, AlertCircle, Users } from 'lucide-react';

export interface ToastMessage {
  id: string;
  type: 'create' | 'update' | 'delete';
  title: string;
  message: string;
  userName?: string;
  meetingId?: string;
  timestamp: number;
}

interface RealtimeToastProps {
  messages: ToastMessage[];
  onClose: (id: string) => void;
  onNavigate?: (meetingId: string) => void;
}

const RealtimeToast: React.FC<RealtimeToastProps> = ({ messages, onClose, onNavigate }) => {
  const [visibleMessages, setVisibleMessages] = useState<ToastMessage[]>([]);

  useEffect(() => {
    setVisibleMessages(messages.slice(-3));
  }, [messages]);

  const getIcon = (type: ToastMessage['type']) => {
    switch (type) {
      case 'create':
        return <Calendar className="w-5 h-5" />;
      case 'update':
        return <CheckCircle className="w-5 h-5" />;
      case 'delete':
        return <Trash2 className="w-5 h-5" />;
      default:
        return <AlertCircle className="w-5 h-5" />;
    }
  };

  const getColor = (type: ToastMessage['type']) => {
    switch (type) {
      case 'create':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'update':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      case 'delete':
        return 'bg-red-50 border-red-200 text-red-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const getIconColor = (type: ToastMessage['type']) => {
    switch (type) {
      case 'create':
        return 'text-green-600';
      case 'update':
        return 'text-blue-600';
      case 'delete':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  if (visibleMessages.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 max-w-md">
      {visibleMessages.map((msg) => (
        <div
          key={msg.id}
          className={`${getColor(msg.type)} border rounded-lg shadow-lg p-4 animate-slide-in-right`}
        >
          <div className="flex items-start space-x-3">
            <div className={`p-2 rounded-lg ${getIconColor(msg.type)} bg-white bg-opacity-50`}>
              {getIcon(msg.type)}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <h4 className="text-sm font-semibold truncate">{msg.title}</h4>
                <button
                  onClick={() => onClose(msg.id)}
                  className="text-gray-400 hover:text-gray-600 transition-colors ml-2"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <p className="text-sm mb-2">{msg.message}</p>

              {msg.userName && (
                <div className="flex items-center space-x-1 text-xs opacity-75">
                  <Users className="w-3 h-3" />
                  <span>Por {msg.userName}</span>
                </div>
              )}

              {msg.meetingId && onNavigate && msg.type !== 'delete' && (
                <button
                  onClick={() => onNavigate(msg.meetingId!)}
                  className="mt-2 text-xs font-medium underline hover:no-underline"
                >
                  Ver reunión
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default RealtimeToast;
